// Telon de membrete para el editor.
//
// El membrete de las plantillas del STJ no vive en el HTML de la plantilla: va
// en la columna `letterhead` y lo pinta el render del PDF como marca de agua
// detras del contenido. Se saco de ahi por dos razones: dentro de
// `content_file` solo existe en la primera pagina (y envolviendo al cuerpo
// impedia paginar), y ademas TinyMCE y HTMLPurifier devuelven solo el HTML
// interno, asi que se perdia en cuanto el administrador guardaba.
//
// El API manda `letterhead_url` apuntando al mismo PNG que usa el PDF
// (public/letterheads en core). Aqui se usa como fondo del area de edicion,
// para que el funcionario escriba viendo el membrete. Es telon: no forma parte
// de lo que se guarda.

/**
 * @param {object} template plantilla tal como la manda el API
 * @returns {{backgroundUrl: string}} vacio si la plantilla no lleva membrete
 */
export const getLetterheadBackdrop = (template) => {
  const url =
    template && typeof template.letterhead_url === 'string' ? template.letterhead_url : '';

  return { backgroundUrl: url };
};

/**
 * CSS que se le pasa a TinyMCE como `content_style`.
 *
 * El fondo se escala al ancho del area de edicion y los margenes van en
 * porcentaje para acompanarlo: el area de escritura del arte arranca a 61mm de
 * 216mm (28%) en acuerdos y a 70mm (32%) en oficios, asi que un 30% sirve para
 * los dos, con 10% a la derecha.
 */
export const buildLetterheadContentStyle = (backgroundUrl) => {
  if (!backgroundUrl) return '';

  return [
    // `height: 100%` en html no es decorativo: sin el, el `min-height: 100%`
    // del body no resuelve (un porcentaje necesita que el padre tenga altura),
    // el body mide solo lo que mide el contenido y el membrete sale cortado con
    // el blanco del iframe debajo.
    'html { background: #ffffff; height: 100%; }',
    'body {',
    `  background-image: url(${backgroundUrl});`,
    // Se repite en vertical para que un escrito largo siga viendose membretado:
    // con `no-repeat` el membrete cubria solo el inicio y el resto del area de
    // escritura quedaba en blanco. Las junturas NO coinciden con los saltos de
    // pagina reales (el arte mide 340.3mm y la hoja legal 355.6), asi que es
    // una ayuda visual para escribir, no una vista previa de paginacion.
    '  background-repeat: repeat-y;',
    '  background-position: top center;',
    '  background-size: 100% auto;',
    '  min-height: 100%;',
    '  margin: 0;',
    '  padding: 18% 10% 10% 30%;',
    '  box-sizing: border-box;',
    '}',
  ].join('\n');
};

/**
 * Estilo de la miniatura del selector.
 *
 * Con el membrete fuera del HTML, la miniatura se quedaria mostrando solo el
 * texto guia y las tres plantillas se verian iguales. Se le pinta el mismo PNG
 * de fondo, escalado a la caja, para que siga reconociendose de un vistazo.
 *
 * El relleno deja el texto dentro del area de escritura del arte en lugar de
 * encimarlo sobre los logos. Va en porcentaje porque la caja es fija (160x240)
 * y el fondo se estira a ella; es una aproximacion de los margenes reales
 * (izquierda 28-32%, derecha ~8%), suficiente a 5px de tipografia.
 *
 * @param {object} baseStyle estilo de la caja (ancho/alto fijos)
 * @param {string} letterheadUrl URL del membrete, o vacio
 */
export const buildThumbnailStyle = (baseStyle, letterheadUrl) => {
  if (!letterheadUrl) return baseStyle;

  return {
    ...baseStyle,
    backgroundImage: `url(${letterheadUrl})`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    padding: '12% 10% 8% 30%',
    boxSizing: 'border-box',
  };
};
